import { useMemo, useRef, useState } from "react";
import {
  Upload,
  Pencil,
  Trash2,
  Plus,
  Check,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { uploadRebatePdf } from "@/lib/api";

const MONTH_LABELS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function formatCurrency(n) {
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    minimumFractionDigits: 2,
  }).format(n || 0);
}

function ymLabel(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  return `${MONTH_LABELS[parseInt(m, 10) - 1]} ${y}`;
}

function amountFor(rebates, viewMode, ym) {
  const r = rebates?.[ym];
  if (r == null) return 0;
  if (typeof r === "number") return viewMode === "non-hcv" ? r : 0;
  return viewMode === "hcv" ? (r.hcv || 0) : (r.nonHcv || 0);
}

function writeAmount(rebates, viewMode, ym, amt) {
  const existing = rebates[ym];
  let normalised;
  if (existing == null) normalised = { hcv: 0, nonHcv: 0 };
  else if (typeof existing === "number") normalised = { hcv: 0, nonHcv: existing };
  else normalised = { hcv: existing.hcv || 0, nonHcv: existing.nonHcv || 0 };
  if (viewMode === "hcv") normalised.hcv = amt;
  else normalised.nonHcv = amt;
  rebates[ym] = normalised;
}

/**
 * GenericRebatesCard — manages a per-month map of monthly rebate totals.
 * Rebates are split by product scope (HCV vs Non-HCV) because the rebate
 * cheques differ between the two — the card only ever shows the scope
 * matching the dashboard's current `viewMode`.
 *
 * Storage shape: { "YYYY-MM": { hcv: number, nonHcv: number } }
 *   (Plain-number values from older sessions are treated as Non-HCV.)
 *
 * @param {Object} props
 * @param {Object} props.rebates
 * @param {(next: Object) => void} props.onChange
 * @param {{ym: string, label: string}[]} props.monthsAvailable
 * @param {"hcv" | "non-hcv"} props.viewMode  Which product scope this card
 *   is currently editing/displaying.
 */
export default function GenericRebatesCard({
  rebates,
  onChange,
  monthsAvailable,
  viewMode = "non-hcv",
  visibleMonths,
  visibleDayCount,
}) {
  const [open, setOpen] = useState(false);
  const [draftMonth, setDraftMonth] = useState("");
  const [draftAmount, setDraftAmount] = useState("");
  const [parsing, setParsing] = useState(false);
  const fileRef = useRef(null);

  // Convenience scoped to this component instance.
  const amt = (ym) => amountFor(rebates, viewMode, ym);

  // If the dashboard supplied a `visibleMonths` filter (i.e. user has a
  // date range selected), constrain the rebate list + summary stats to
  // ONLY those months. With no filter we fall back to "all months with
  // a rebate" (legacy behaviour).
  const visibleMonthSet = useMemo(
    () =>
      Array.isArray(visibleMonths) && visibleMonths.length > 0
        ? new Set(visibleMonths)
        : null,
    [visibleMonths]
  );

  // Months that actually have a rebate in the current scope AND fall
  // inside the active date filter.
  const ymList = useMemo(
    () =>
      Object.keys(rebates)
        .filter((ym) => amountFor(rebates, viewMode, ym) > 0)
        .filter((ym) => (visibleMonthSet ? visibleMonthSet.has(ym) : true))
        .sort((a, b) => (a < b ? 1 : -1)),
    [rebates, viewMode, visibleMonthSet]
  );

  const scopeLabel = viewMode === "hcv" ? "HCV" : "Non-HCV";

  const months = useMemo(() => {
    // Provide both report months and any rebate months not in the report.
    const set = new Map();
    monthsAvailable.forEach((m) => set.set(m.ym, m.label));
    Object.keys(rebates).forEach((ym) => {
      if (!set.has(ym)) set.set(ym, ymLabel(ym));
    });
    return Array.from(set.entries())
      .sort(([a], [b]) => (a < b ? 1 : a > b ? -1 : 0))
      .map(([ym, label]) => ({ ym, label }));
  }, [monthsAvailable, rebates]);

  const totalAcrossMonths = useMemo(
    () => ymList.reduce((s, ym) => s + amountFor(rebates, viewMode, ym), 0),
    [ymList, rebates, viewMode]
  );
  const monthsWithRebate = ymList.length;
  const avgMonthly = monthsWithRebate > 0 ? totalAcrossMonths / monthsWithRebate : 0;
  const projectedAnnual = avgMonthly * 12;
  // Per-day average across the selected reporting period — uses the day
  // count the dashboard is currently filtering on so the math reflects
  // partial months (e.g. "May 4 → June 25" = 53 days, not 2 × 30).
  const dailyAvg =
    visibleDayCount && visibleDayCount > 0
      ? totalAcrossMonths / visibleDayCount
      : 0;
  const periodLabel = visibleMonthSet
    ? `${monthsWithRebate} mo · ${visibleDayCount || 0}d`
    : "all months";

  const openForAdd = () => {
    setDraftMonth(monthsAvailable[0]?.ym || "");
    setDraftAmount("");
    setMultiEntries(null);
    setOpen(true);
  };

  const openForEdit = (ym) => {
    setDraftMonth(ym);
    setDraftAmount(String(amt(ym) || ""));
    setMultiEntries(null);
    setOpen(true);
  };

  const removeRebate = (ym) => {
    const next = { ...rebates };
    writeAmount(next, viewMode, ym, 0);
    const r = next[ym];
    if (r && typeof r === "object" && (r.hcv || 0) === 0 && (r.nonHcv || 0) === 0) {
      delete next[ym];
    }
    onChange(next);
    toast.success(`${scopeLabel} rebate removed for ${ymLabel(ym)}`);
  };

  // When a multi-month rebate PDF is detected, we surface the per-month list
  // in the dialog with a single "Save all" action — much cleaner than asking
  // the user to walk through them one by one.
  const [multiEntries, setMultiEntries] = useState(null);

  const handlePdfPick = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please upload a PDF file.");
      return;
    }
    setParsing(true);
    try {
      const res = await uploadRebatePdf(file);
      if (Array.isArray(res.entries) && res.entries.length > 1) {
        // Multi-month PDF — defer to the bulk confirmation UI.
        setMultiEntries(res.entries);
        setDraftAmount("");
        setDraftMonth("");
        toast.success(
          `Detected ${res.entries.length} months · ${formatCurrency(res.amount)} total`
        );
      } else {
        setDraftAmount(res.amount.toFixed(2));
        if (res.suggested_month) setDraftMonth(res.suggested_month);
        toast.success(
          `Detected ${formatCurrency(res.amount)}${
            res.suggested_month ? ` for ${ymLabel(res.suggested_month)}` : ""
          }`
        );
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Failed to parse rebate PDF.";
      toast.error(String(detail));
    } finally {
      setParsing(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const saveMulti = () => {
    if (!multiEntries || multiEntries.length === 0) return;
    const next = { ...rebates };
    let added = 0;
    let updated = 0;
    multiEntries.forEach((e) => {
      if (e.amount > 0 && /^\d{4}-\d{2}$/.test(e.month)) {
        if (amt(e.month) > 0) updated += 1;
        else added += 1;
        writeAmount(next, viewMode, e.month, e.amount);
      }
    });
    onChange(next);
    setMultiEntries(null);
    setOpen(false);
    toast.success(
      `Saved ${added + updated} months of ${scopeLabel} rebates (${added} new, ${updated} updated)`
    );
  };

  const saveDraft = () => {
    const amtVal = parseFloat(draftAmount);
    if (!draftMonth || !/^\d{4}-\d{2}$/.test(draftMonth)) {
      toast.error("Pick a month first.");
      return;
    }
    if (!isFinite(amtVal) || amtVal <= 0) {
      toast.error("Enter a valid rebate amount.");
      return;
    }
    const next = { ...rebates };
    writeAmount(next, viewMode, draftMonth, amtVal);
    onChange(next);
    setOpen(false);
    toast.success(
      `Saved ${formatCurrency(amtVal)} ${scopeLabel} rebate for ${ymLabel(draftMonth)}`
    );
  };

  return (
    <div
      data-testid="generic-rebates-card"
      className="bg-white border border-[#E6EBE8] rounded-xl overflow-hidden"
    >
      <div className="p-5 sm:p-6 border-b border-[#F0F4F2] flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] text-[#5C746A]">
            {scopeLabel} purchasing rebates
            <span className="ml-2 text-[#A3B8AD] font-medium normal-case tracking-normal">
              · {periodLabel}
            </span>
          </div>
          <div className="mt-1 flex items-baseline gap-3 flex-wrap">
            <div className="font-mono-data text-2xl sm:text-3xl font-light tracking-tighter text-[#1B3B2E]">
              {formatCurrency(avgMonthly)}
              <span className="text-xs sm:text-sm text-[#8A9C93] ml-1.5">/mo avg</span>
            </div>
            {dailyAvg > 0 && (
              <div className="text-[11px] sm:text-xs text-[#5C746A]">
                Daily avg:{" "}
                <span className="font-mono-data text-[#1B3B2E]">
                  {formatCurrency(dailyAvg)}
                </span>
              </div>
            )}
            {monthsWithRebate > 0 && (
              <div className="text-[11px] sm:text-xs text-[#5C746A]">
                Total:{" "}
                <span className="font-mono-data text-[#1B3B2E]">
                  {formatCurrency(totalAcrossMonths)}
                </span>
              </div>
            )}
            {!visibleMonthSet && monthsWithRebate > 0 && (
              <div className="text-[11px] sm:text-xs text-[#5C746A]">
                Projected annual:{" "}
                <span className="font-mono-data text-[#1B3B2E]">
                  {formatCurrency(projectedAnnual)}
                </span>
              </div>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={openForAdd}
          data-testid="add-rebate-button"
          className="no-print inline-flex items-center gap-1.5 rounded-lg border border-[#2B5E4A] bg-[#2B5E4A] text-white px-3 py-2 text-xs font-semibold hover:bg-[#1B3B2E] transition-colors"
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={2.25} />
          Add {scopeLabel} rebate
        </button>
      </div>

      {ymList.length === 0 ? (
        <div className="px-5 sm:px-6 py-6 text-center">
          <div className="text-xs sm:text-sm text-[#5C746A] max-w-sm mx-auto leading-snug">
            Upload your monthly {scopeLabel} purchasing-rebate PDF or enter the
            amount manually. We&apos;ll fold it into your monthly P&amp;L below.
          </div>
        </div>
      ) : (
        <div className="divide-y divide-[#F0F4F2]" data-testid="rebate-rows">
          {ymList.map((ym) => (
            <div
              key={ym}
              className="px-5 sm:px-6 py-3 flex items-center justify-between gap-3"
              data-testid={`rebate-row-${ym}`}
            >
              <div className="min-w-0">
                <div className="text-sm font-medium text-[#1B3B2E]">
                  {ymLabel(ym)}
                </div>
                <div className="text-[11px] text-[#8A9C93]">{scopeLabel} purchasing rebate</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="font-mono-data text-sm sm:text-base font-medium text-[#1B3B2E]">
                  {formatCurrency(amt(ym))}
                </div>
                <button
                  type="button"
                  onClick={() => openForEdit(ym)}
                  aria-label={`Edit rebate for ${ymLabel(ym)}`}
                  className="no-print inline-flex items-center justify-center h-8 w-8 rounded-lg text-[#5C746A] hover:bg-[#F0F4F2] hover:text-[#1B3B2E] transition-colors"
                  data-testid={`rebate-edit-${ym}`}
                >
                  <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
                </button>
                <button
                  type="button"
                  onClick={() => removeRebate(ym)}
                  aria-label={`Remove rebate for ${ymLabel(ym)}`}
                  className="no-print inline-flex items-center justify-center h-8 w-8 rounded-lg text-[#5C746A] hover:bg-[#FFF2EF] hover:text-[#D36C56] transition-colors"
                  data-testid={`rebate-remove-${ym}`}
                >
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white sm:max-w-md" data-testid="rebate-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1B3B2E] font-heading">
              Add monthly {scopeLabel} rebate
            </DialogTitle>
            <DialogDescription className="text-[#5C746A] text-xs">
              Upload the {scopeLabel} rebate PDF or type the deposit amount,
              then choose the month it covers. {scopeLabel === "HCV"
                ? "HCV rebates are tracked separately from regular rebates."
                : "Regular (Non-HCV) rebates are tracked separately from HCV rebates."}
            </DialogDescription>
          </DialogHeader>

          {/* Hidden file input for the PDF upload action */}
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            data-testid="rebate-file-input"
            onChange={(e) => handlePdfPick(e.target.files?.[0])}
          />

          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={parsing}
            data-testid="rebate-upload-pdf"
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-dashed border-[#2B5E4A] bg-[#F7F9F8] px-4 py-3 text-sm font-medium text-[#1B3B2E] hover:bg-[#EFF4F1] transition-colors disabled:opacity-60"
          >
            {parsing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
                Parsing PDF…
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" strokeWidth={1.75} />
                Upload rebate PDF
              </>
            )}
          </button>

          {/* Multi-month detected — bulk-save confirmation list */}
          {multiEntries && multiEntries.length > 1 ? (
            <div className="space-y-3" data-testid="rebate-multi-summary">
              <div className="text-[10px] uppercase tracking-[0.15em] text-[#2B5E4A] font-semibold">
                Detected {multiEntries.length} months
              </div>
              <div className="rounded-lg border border-[#E6EBE8] bg-[#F7F9F8] divide-y divide-[#E6EBE8] overflow-hidden">
                {multiEntries.map((e) => {
                  const isOverwrite = amt(e.month) > 0;
                  return (
                    <div
                      key={e.month}
                      className="flex items-center justify-between gap-2 px-3 py-2"
                      data-testid={`rebate-multi-row-${e.month}`}
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-[#1B3B2E]">
                          {ymLabel(e.month)}
                        </div>
                        {isOverwrite && (
                          <div className="text-[10px] text-[#B65A45]">
                            Will overwrite existing {scopeLabel} {formatCurrency(amt(e.month))}
                          </div>
                        )}
                      </div>
                      <div className="font-mono-data text-sm text-[#1B3B2E]">
                        {formatCurrency(e.amount)}
                      </div>
                    </div>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={saveMulti}
                data-testid="rebate-save-all"
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#1B3B2E] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#0F2A20] transition-colors"
              >
                <Check className="h-4 w-4" strokeWidth={2} />
                Save all {multiEntries.length} months
              </button>
            </div>
          ) : (
            <>
              <div className="relative my-1">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-[#F0F4F2]" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-2 text-[10px] uppercase tracking-[0.18em] text-[#A3B8AD] font-semibold">
                    or enter manually
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] text-[#5C746A] font-semibold">
                    Month
                  </label>
                  <select
                    value={draftMonth}
                    onChange={(e) => setDraftMonth(e.target.value)}
                    data-testid="rebate-month-select"
                    className="mt-1 w-full rounded-lg border border-[#E6EBE8] bg-white px-3 py-2 text-sm text-[#1B3B2E] focus:outline-none focus:ring-2 focus:ring-[#2B5E4A]/40"
                  >
                <option value="">Select a month…</option>
                {months.map((m) => (
                  <option key={m.ym} value={m.ym}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-[#5C746A] font-semibold">
                Total rebate (CAD)
              </label>
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={draftAmount}
                onChange={(e) => setDraftAmount(e.target.value)}
                placeholder="0.00"
                data-testid="rebate-amount-input"
                className="mt-1 w-full rounded-lg border border-[#E6EBE8] bg-white px-3 py-2 text-sm font-mono-data text-[#1B3B2E] focus:outline-none focus:ring-2 focus:ring-[#2B5E4A]/40"
              />
            </div>
          </div>

          <button
            type="button"
            onClick={saveDraft}
            data-testid="rebate-save"
            className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#1B3B2E] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#0F2A20] transition-colors"
          >
            <Check className="h-4 w-4" strokeWidth={2} />
            Save rebate
          </button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
